
import { useState, useEffect } from 'react';
import { useI18n } from '@/lib/i18n';
import OfflineCache from '@/lib/offline-cache';
import { WifiOff, RotateCw, CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";

interface Props {
    children: React.ReactNode;
    feature: string;
    cacheKey?: string;
    ttlHours?: number; // Default 24
}

export default function FeatureGuard({ children, feature, cacheKey, ttlHours = 24 }: Props) {
    const { t } = useI18n();
    const [status, setStatus] = useState<'loading' | 'live' | 'cached' | 'offline'>('loading');
    const [cachedData, setCachedData] = useState<any>(null);
    const [timestamp, setTimestamp] = useState<number | null>(null);

    const checkFeature = async () => {
        setStatus('loading');

        const MAX_RETRIES = 2;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                // 1. LIVE DB check (Fail fast 10s - account for potential cold starts)
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000);

                const res = await fetch(`/api/health/${feature}`, {
                    cache: 'no-store',
                    signal: controller.signal
                });
                clearTimeout(timeoutId);

                if (res.ok) {
                    setStatus('live');
                    return;
                }
            } catch (error) {
                console.warn(`[FeatureGuard] Attempt ${attempt} failed for ${feature}:`, error);
                // If it was an abort/timeout and we have retries left, wait a bit
                if (attempt < MAX_RETRIES) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    continue;
                }
            }
        }

        // 2. CACHED DATA Fallback
        if (cacheKey) {
            const cached = OfflineCache.get(cacheKey);
            const meta = OfflineCache.getMeta(cacheKey);
            if (cached) {
                setCachedData(cached);
                if (meta) setTimestamp(meta.timestamp);
                setStatus('cached');
                return;
            }
        }

        // 3. OFFLINE
        setStatus('offline');
    };

    useEffect(() => {
        checkFeature();
        // Auto-retry every 60s if offline
        const interval = setInterval(() => {
            if (status === 'offline' || status === 'cached') {
                checkFeature();
            }
        }, 60000);
        return () => clearInterval(interval);
    }, [feature, cacheKey]);

    // Loading Skeleton
    if (status === 'loading' && !cachedData) {
        return (
            <div className="animate-pulse p-8 bg-muted/20 rounded-2xl border border-muted">
                <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-muted rounded w-2/3 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
        );
    }

    // LIVE: Just render children
    if (status === 'live') {
        return <>{children}</>;
    }

    // CACHED VIEW
    if (status === 'cached') {
        return (
            <div className="relative">
                {/* Floating Status Banner */}
                <div className="bg-orange-100 dark:bg-orange-900/30 border-b border-orange-200 text-orange-800 dark:text-orange-200 px-4 py-2 text-sm flex items-center justify-between mb-4 rounded-t-lg">
                    <div className="flex items-center gap-2">
                        <WifiOff className="h-4 w-4" />
                        <span>
                            <strong>{t("common.offlineMode")}</strong> • Gegevens van {timestamp ? new Date(timestamp).toLocaleString('nl-NL') : 'eerder'}
                        </span>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={checkFeature}
                        className="h-6 px-2 text-xs hover:bg-orange-200 dark:hover:bg-orange-800"
                    >
                        <RotateCw className="h-3 w-3 mr-1" />
                        Check Live
                    </Button>
                </div>

                {/* Render the actual content (which assumes data is available via its own query, 
                  but we rely on the cache inside the child component or props) 
                  
                  IMPORTANT: The children need to know to USE the cache if we are in this state.
                  However, `FeatureGuard` wraps the whole page. The queries inside `children` 
                  (tanstack query) will fail. 
                  
                  We can't easily inject props into standard children. 
                  
                  STRATEGY: We render children anyway. The `useQuery` inside them 
                  should be configured to use `initialData` from OfflineCache 
                  OR `placeholderData`.
              */}
                <div className="opacity-90">
                    {children}
                </div>
            </div>
        );
    }

    // OFFLINE (No Cache)
    return (
        <div className="p-8 bg-destructive/5 rounded-2xl border border-destructive/20 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 mb-4">
                <WifiOff className="h-6 w-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
                {feature === 'scanner' ? 'Scanner tijdelijk niet beschikbaar' :
                    feature === 'shops' ? 'Kaart offline' :
                        'Geen verbinding'}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                We kunnen geen verbinding maken met de database. Probeer het later opnieuw.
            </p>

            <Button onClick={checkFeature} variant="outline" className="gap-2">
                <RotateCw className="h-4 w-4" />
                Opnieuw proberen
            </Button>
        </div>
    );
}
